/**
 * src/shell/gallery/GalleryWorkspace.tsx
 *
 * Sovereign Gallery Workspace & Accessible Lightbox Media Viewer.
 *
 * Invariant: Full accessibility (a11y) compliance with ARIA modal semantics,
 * strict focus trapping, bidirectional arrow key navigation, Escape dismissal,
 * and automatic focus restoration to the triggering thumbnail.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface GalleryMediaItem {
  id: string;
  title: string;
  src: string;
  mimeType: string;
  tags?: string[];
}

export interface GalleryWorkspaceProps {
  initialItems?: GalleryMediaItem[];
}

const DEFAULT_ITEMS: GalleryMediaItem[] = [
  { id: 'img-1', title: 'Infra Blueprint A', src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%231e293b"/><text x="50%" y="50%" fill="%2338bdf8" font-size="20" text-anchor="middle">Blueprint A</text></svg>', mimeType: 'image/svg+xml' },
  { id: 'img-2', title: 'Topology Mesh B', src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%230f172a"/><text x="50%" y="50%" fill="%2334d399" font-size="20" text-anchor="middle">Topology B</text></svg>', mimeType: 'image/svg+xml' },
  { id: 'img-3', title: 'Security Perimeter C', src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%231e1b4b"/><text x="50%" y="50%" fill="%23a855f7" font-size="20" text-anchor="middle">Security C</text></svg>', mimeType: 'image/svg+xml' },
];

export const GalleryWorkspace: React.FC<GalleryWorkspaceProps> = ({
  initialItems = DEFAULT_ITEMS,
}) => {
  const [items] = useState<GalleryMediaItem[]>(initialItems.length > 0 ? initialItems : DEFAULT_ITEMS);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);

  const isLightboxOpen = activeIdx !== null;
  const currentItem = isLightboxOpen && activeIdx !== null ? items[activeIdx] : null;

  // Open Lightbox and remember triggering element
  const openLightbox = (index: number) => {
    setActiveIdx(index);
  };

  // Close Lightbox and restore focus
  const closeLightbox = useCallback(() => {
    if (activeIdx !== null) {
      const triggeringItem = items[activeIdx];
      setActiveIdx(null);
      if (triggeringItem) {
        // Restore focus to thumbnail
        setTimeout(() => {
          triggerRefs.current.get(triggeringItem.id)?.focus();
        }, 10);
      }
    }
  }, [activeIdx, items]);

  const handleNext = useCallback(() => {
    if (activeIdx !== null && activeIdx < items.length - 1) {
      setActiveIdx(activeIdx + 1);
    }
  }, [activeIdx, items.length]);

  const handlePrev = useCallback(() => {
    if (activeIdx !== null && activeIdx > 0) {
      setActiveIdx(activeIdx - 1);
    }
  }, [activeIdx]);

  // Focus close button on lightbox open
  useEffect(() => {
    if (isLightboxOpen) {
      setTimeout(() => {
        closeBtnRef.current?.focus();
      }, 20);
    }
  }, [isLightboxOpen]);

  // Keyboard navigation & Focus Trapping
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
        return;
      }

      // Focus Trap inside Dialog
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute('disabled'));

        if (focusable.length === 0) return;

        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, closeLightbox, handleNext, handlePrev]);

  return (
    <div style={{ padding: '24px', background: 'var(--mesh-color-bg, #0b1120)', minHeight: '100vh', color: 'var(--mesh-color-text, #f8fafc)' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>🎨 Sovereign Gallery Workspace</h1>
          <p style={{ fontSize: '0.85rem', color: 'var(--mesh-color-text-muted, #94a3b8)', marginTop: 4 }}>
            High-Resolution Media Catalog &amp; Provenance Asset Studio
          </p>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--mesh-color-primary, #38bdf8)' }}>
          {items.length} Assets Loaded
        </div>
      </div>

      {/* Grid of Thumbnails */}
      <div data-testid="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
        {items.map((item, index) => (
          <button
            key={item.id}
            data-testid={`gallery-thumb-${index}`}
            ref={(el) => {
              if (el) triggerRefs.current.set(item.id, el);
              else triggerRefs.current.delete(item.id);
            }}
            onClick={() => openLightbox(index)}
            aria-label={item.title}
            style={{
              background: 'var(--mesh-color-surface, #0f172a)',
              border: '1px solid var(--mesh-color-border, #334155)',
              borderRadius: 'var(--mesh-radius-md, 8px)',
              padding: '12px',
              cursor: 'pointer',
              textAlign: 'left',
              color: 'inherit',
              transition: 'all 0.15s ease',
            }}
          >
            <div style={{ height: '140px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
              <img
                src={item.src}
                alt=""
                aria-hidden="true"
                role="presentation"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px' }}>{item.title}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--mesh-color-text-muted, #94a3b8)' }}>{item.mimeType}</div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal Dialog */}
      {isLightboxOpen && currentItem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Media Viewer"
            style={{
              background: 'var(--mesh-color-surface, #0f172a)',
              border: '1px solid var(--mesh-color-border, #334155)',
              borderRadius: 'var(--mesh-radius-lg, 12px)',
              padding: '24px',
              maxWidth: '800px',
              width: '90%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
            }}
          >
            {/* Header & Controls */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div data-testid="lightbox-index" style={{ fontSize: '0.85rem', color: 'var(--mesh-color-text-muted, #94a3b8)' }}>
                {activeIdx! + 1} / {items.length}
              </div>

              <button
                ref={closeBtnRef}
                onClick={closeLightbox}
                aria-label="Close media viewer"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--mesh-color-border, #334155)',
                  borderRadius: 'var(--mesh-radius-sm, 4px)',
                  color: 'var(--mesh-color-text, #f8fafc)',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                Close (Esc)
              </button>
            </div>

            {/* Media Image View */}
            <div style={{ width: '100%', maxHeight: '450px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', marginBottom: '16px' }}>
              <img
                src={currentItem.src}
                alt={currentItem.title}
                role="img"
                style={{ maxWidth: '100%', maxHeight: '420px', objectFit: 'contain', borderRadius: '4px' }}
              />
            </div>

            {/* Bottom Info & Navigation */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{currentItem.title}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  ref={prevBtnRef}
                  onClick={handlePrev}
                  disabled={activeIdx === 0}
                  aria-label="Previous image"
                  style={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '4px',
                    color: activeIdx === 0 ? '#64748b' : '#f8fafc',
                    padding: '6px 14px',
                    cursor: activeIdx === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ◀ Prev
                </button>
                <button
                  ref={nextBtnRef}
                  onClick={handleNext}
                  disabled={activeIdx === items.length - 1}
                  aria-label="Next image"
                  style={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '4px',
                    color: activeIdx === items.length - 1 ? '#64748b' : '#f8fafc',
                    padding: '6px 14px',
                    cursor: activeIdx === items.length - 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next ▶
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
